import { Modal, Table, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { User } from '../../services/user.service';
import { format } from 'date-fns';
import { ja, enUS } from 'date-fns/locale';

const getDateFnsLocale = (lang: string) => {
  switch (lang) {
    case 'ja':
      return ja;
    case 'en':
      return enUS;
    default:
      return enUS;
  }
};

interface UserDetailModalProps {
  show: boolean;
  onHide: () => void;
  user: User | null;
}

export default function UserDetailModal({ show, onHide, user }: UserDetailModalProps) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  if (!user) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-user me-2"></i>
          {t('users.user_details', { defaultValue: 'ユーザー詳細' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table striped bordered size="sm">
          <tbody>
            <tr>
              <th style={{ width: '30%' }}>{t('common.id')}</th>
              <td>{user.id}</td>
            </tr>
            <tr>
              <th>{t('users.col_name', { defaultValue: 'ユーザー名' })}</th>
              <td>{user.username}</td>
            </tr>
            <tr>
              <th>{t('users.col_email')}</th>
              <td>{user.email}</td>
            </tr>
            <tr>
              <th>{t('profile.first_name', { defaultValue: '名' })}</th>
              <td>{user.firstName || '-'}</td>
            </tr>
            <tr>
              <th>{t('profile.last_name', { defaultValue: '姓' })}</th>
              <td>{user.lastName || '-'}</td>
            </tr>
            <tr>
              <th>{t('users.col_role')}</th>
              <td>{user.role?.name || '-'}</td>
            </tr>
            <tr>
              <th>{t('user.organization', { defaultValue: '組織名' })}</th>
              <td>{user.organizationName || '-'}</td>
            </tr>
            <tr>
              <th>{t('user.department', { defaultValue: '部署名' })}</th>
              <td>{user.departmentName || '-'}</td>
            </tr>
            <tr>
              <th>{t('users.col_status')}</th>
              <td>
                {user.isActive ? (
                  <Badge bg="success">{t('users.status_active')}</Badge>
                ) : (
                  <Badge bg="secondary">{t('users.status_inactive')}</Badge>
                )}
              </td>
            </tr>
            <tr>
              <th>{t('profile.timezone', { defaultValue: 'タイムゾーン' })}</th>
              <td>{user.timezone || '-'}</td>
            </tr>
            <tr>
              <th>{t('profile.language', { defaultValue: '言語' })}</th>
              <td>{user.language || '-'}</td>
            </tr>
            <tr>
              <th>{t('users.col_last_login')}</th>
              <td>
                {user.lastLogin
                  ? format(new Date(user.lastLogin), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })
                  : '-'}
              </td>
            </tr>
            <tr>
              <th>{t('users.col_created_at')}</th>
              <td>
                {user.createdAt
                  ? format(new Date(user.createdAt), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })
                  : '-'}
              </td>
            </tr>
            <tr>
              <th>{t('common.updated_at', { defaultValue: '更新日時' })}</th>
              <td>
                {user.updatedAt
                  ? format(new Date(user.updatedAt), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })
                  : '-'}
              </td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onHide}>
          {t('common.close', { defaultValue: '閉じる' })}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

